import React, { useState, useRef, useEffect } from 'react';

import Canvas from './canvas';
import Graph, { GraphNode } from './graph';
import { drawGraph } from './canvas-util';

type Props = {
  type: 'bfs' | 'dfs';
};

const GraphCanvas: React.FC<Props> = ({ type }) => {
  const levelColors = [
    '#fdd835',
    '#fdd835',
    '#ffb300',
    '#ffb300',
    '#ffb300',
    '#fb8c00',
    '#fb8c00',
  ];
  const x = [0.3, 0.5, 0.7];
  const y = [0.3, 0.5, 0.7];
  const coords = [
    [x[0], y[0]],
    [x[1], y[0]],
    [x[0], y[1]],
    [x[1], y[1]],
    [x[0], y[2]],
    [x[1], y[2]],
    [x[2], y[1]],
  ];

  const nodes: GraphNode[] = [];
  for (let i = 0; i < 7; ++i) {
    if (i > coords.length - 1) break;
    nodes.push(new GraphNode(`v${i + 1}`, { coords: coords[i] }));
  }

  const graph = new Graph(nodes);
  graph.addUndirectedEdge(1, 2);
  graph.addUndirectedEdge(1, 3);
  graph.addUndirectedEdge(2, 4);
  graph.addUndirectedEdge(2, 7);
  graph.addUndirectedEdge(3, 5);
  if (type === 'bfs') {
    graph.addUndirectedEdge(4, 6);
  }
  graph.addUndirectedEdge(5, 6);
  graph.addUndirectedEdge(6, 7);
  if (type === 'dfs') {
    graph.addUndirectedEdge(4, 6);
  }

  const searchOrderResult = type === 'bfs' ? graph.bfs() : graph.dfs();

  const [colors, setColors] = useState(searchOrderResult.map(() => '#fff'));
  const indexRef = useRef(0);
  const frameRef = useRef(0);
  const requestRef = useRef(null);
  const delayFrame = 20;

  const animate = () => {
    if (frameRef.current >= delayFrame) {
      if (indexRef.current > colors.length) {
        indexRef.current = 0;
        setColors(prev => [...prev.map(() => '#fff')]);
      } else {
        setColors(prev => {
          prev[indexRef.current] = levelColors[indexRef.current];
          return [...prev];
        });
        indexRef.current += 1;
      }
      frameRef.current = 0;
    }
    frameRef.current += 1;
    requestRef.current = requestAnimationFrame(animate);
  };

  useEffect(() => {
    requestRef.current = requestAnimationFrame(animate);
    return () => cancelAnimationFrame(requestRef.current);
  }, []);

  const draw = (
    canvasRef: React.MutableRefObject<HTMLCanvasElement>,
    ctx: CanvasRenderingContext2D
  ): void => {
    drawGraph(canvasRef.current, ctx, searchOrderResult, { colors });
  };

  return <Canvas paint={draw} />;
};

export default GraphCanvas;
